import { useState } from 'react';
import { smallVariant, srcSetFor } from '@/lib/images';

/**
 * FramedImage — shows an image fully contained (never cropped) with a scaled,
 * blurred copy of itself filling the sides. Fills its positioned parent
 * (which owns the aspect ratio / rounded corners / overflow: hidden).
 * If the image fails to load (missing photo), it falls back to a branded
 * placeholder instead of a broken-image icon.
 * Two-size photos (`<name>-640.webp` + `<name>-<W>.webp`, see `lib/images`) get a
 * `srcset` automatically; pass `sizes` to tell the browser the slot width.
 */
export function FramedImage({
  src,
  alt,
  loading,
  className,
  fallback,
  sizes,
}: {
  src: string;
  alt: string;
  loading?: 'lazy' | 'eager';
  className?: string;
  /** Label shown if the image fails to load (e.g. the brand name). */
  fallback?: string;
  /** Slot width hint for the srcset, e.g. "(min-width: 768px) 33vw, 100vw". */
  sizes?: string;
}) {
  const [errored, setErrored] = useState(false);
  const srcSet = srcSetFor(src);

  if (errored) {
    return (
      <span className={`framed framed--fallback${className ? ` ${className}` : ''}`} aria-label={alt}>
        {fallback ?? 'CARO'}
      </span>
    );
  }

  return (
    <span className={`framed${className ? ` ${className}` : ''}`}>
      <img
        className="framed__bg"
        // The blurred backdrop never needs the large file.
        src={smallVariant(src)}
        alt=""
        aria-hidden="true"
        loading={loading}
        onError={() => setErrored(true)}
      />
      <img
        className="framed__img"
        src={src}
        srcSet={srcSet}
        sizes={srcSet ? (sizes ?? '100vw') : undefined}
        alt={alt}
        loading={loading}
        onError={() => setErrored(true)}
      />
    </span>
  );
}
